var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { emptyObject } from "@contember/react-utils";
import { Parser } from "./Parser.js";
import { GraphQlLiteral } from "@contember/client";
import { EntityListPreferencesDefaults } from "../treeParameters/EntityListPreferences.js";
import { TreeParameterMerger } from "../core/TreeParameterMerger.js";
import { RelationDefaults } from "../treeParameters/Relation.js";
import { EntityCreationParametersDefaults } from "../treeParameters/EntityCreationParameters.js";
import { QualifiedEntityParametersDefaults } from "../treeParameters/QualifiedEntityParameters.js";
import { VariableInputTransformer } from "../core/VariableInputTransformer.js";
import { LeafFieldDefaults } from "../treeParameters/LeafField.js";
import { EventListenersStore } from "../treeParameters/EventListenersStore.js";
const _QueryLanguage = class {
  static preparePrimitiveEntryPoint(entryPoint) {
    return (input, environment) => Parser.parseQueryLanguageExpression(input, entryPoint, environment);
  }
  static preparePrimitiveEntryPointWithFallback(entryPoint) {
    return (input, environment) => {
      if (typeof input === "string") {
        return Parser.parseQueryLanguageExpression(input, entryPoint, environment);
      }
      return input;
    };
  }
  static desugarEntityListParameters(sugarablePart, unsugarablePart, environment) {
    return {
      filter: sugarablePart.filter ? this.desugarFilter(sugarablePart.filter, environment) : void 0,
      limit: unsugarablePart.limit,
      offset: unsugarablePart.offset,
      orderBy: unsugarablePart.orderBy ? this.desugarOrderBy(unsugarablePart.orderBy, environment) : void 0,
      initialEntityCount: unsugarablePart.initialEntityCount ?? EntityListPreferencesDefaults.initialEntityCount
    };
  }
  static desugarSetOnCreate(setOnCreate, environment) {
    if (Array.isArray(setOnCreate)) {
      const whereList = setOnCreate.map((connection) => this.desugarUniqueWhere(connection, environment));
      return whereList.reduce(
        (accumulator, uniqueWhere) => TreeParameterMerger.mergeSetOnCreate(accumulator, uniqueWhere)
      );
    }
    if (typeof setOnCreate === "string") {
      return this.desugarUniqueWhere(setOnCreate, environment);
    }
    return setOnCreate;
  }
  static desugarEventListener(listener) {
    if (typeof listener === "function") {
      return /* @__PURE__ */ new Set([listener]);
    }
    return listener;
  }
  static desugarSingleEntityEventListeners(unsugarable) {
    if (unsugarable.onBeforePersist === void 0 && unsugarable.onBeforeUpdate === void 0 && unsugarable.onInitialize === void 0 && unsugarable.onPersistError === void 0 && unsugarable.onPersistSuccess === void 0 && unsugarable.onUpdate === void 0) {
      return void 0;
    }
    const store = new EventListenersStore();
    if (unsugarable.onConnectionUpdate) {
      for (const fieldName in unsugarable.onConnectionUpdate) {
        const listener = this.desugarEventListener(unsugarable.onConnectionUpdate[fieldName]);
        store.set({ type: "connectionUpdate", key: fieldName }, listener);
      }
    }
    const beforePersist = this.desugarEventListener(unsugarable.onBeforePersist);
    const beforeUpdate = this.desugarEventListener(unsugarable.onBeforeUpdate);
    const initialize = this.desugarEventListener(unsugarable.onInitialize);
    const persistError = this.desugarEventListener(unsugarable.onPersistError);
    const persistSuccess = this.desugarEventListener(unsugarable.onPersistSuccess);
    const update = this.desugarEventListener(unsugarable.onUpdate);
    if (beforePersist) {
      store.set({ type: "beforePersist" }, beforePersist);
    }
    if (beforeUpdate) {
      store.set({ type: "beforeUpdate" }, beforeUpdate);
    }
    if (initialize) {
      store.set({ type: "initialize" }, initialize);
    }
    if (persistError) {
      store.set({ type: "persistError" }, persistError);
    }
    if (persistSuccess) {
      store.set({ type: "persistSuccess" }, persistSuccess);
    }
    if (update) {
      store.set({ type: "update" }, update);
    }
    return store;
  }
  static desugarEntityListEventListeners(unsugarable) {
    if (unsugarable.onBeforePersist === void 0 && unsugarable.onBeforeUpdate === void 0 && unsugarable.onChildBeforeUpdate === void 0 && unsugarable.onChildInitialize === void 0 && unsugarable.onChildUpdate === void 0 && unsugarable.onInitialize === void 0 && unsugarable.onPersistError === void 0 && unsugarable.onPersistSuccess === void 0 && unsugarable.onUpdate === void 0) {
      return void 0;
    }
    const eventListeners = new EventListenersStore();
    const beforePersist = this.desugarEventListener(unsugarable.onBeforePersist);
    const beforeUpdate = this.desugarEventListener(unsugarable.onBeforeUpdate);
    const initialize = this.desugarEventListener(unsugarable.onInitialize);
    const persistError = this.desugarEventListener(unsugarable.onPersistError);
    const persistSuccess = this.desugarEventListener(unsugarable.onPersistSuccess);
    const update = this.desugarEventListener(unsugarable.onUpdate);
    if (beforePersist) {
      eventListeners.set({ type: "beforePersist" }, beforePersist);
    }
    if (beforeUpdate) {
      eventListeners.set({ type: "beforeUpdate" }, beforeUpdate);
    }
    if (initialize) {
      eventListeners.set({ type: "initialize" }, initialize);
    }
    if (persistError) {
      eventListeners.set({ type: "persistError" }, persistError);
    }
    if (persistSuccess) {
      eventListeners.set({ type: "persistSuccess" }, persistSuccess);
    }
    if (update) {
      eventListeners.set({ type: "update" }, update);
    }
    const childEventListeners = new EventListenersStore();
    const childBeforeUpdate = this.desugarEventListener(unsugarable.onChildBeforeUpdate);
    const childInitialize = this.desugarEventListener(unsugarable.onChildInitialize);
    const childUpdate = this.desugarEventListener(unsugarable.onChildUpdate);
    if (childBeforeUpdate) {
      childEventListeners.set({ type: "beforeUpdate" }, childBeforeUpdate);
    }
    if (childInitialize) {
      childEventListeners.set({ type: "initialize" }, childInitialize);
    }
    if (childUpdate) {
      childEventListeners.set({ type: "update" }, childUpdate);
    }
    return {
      eventListeners,
      childEventListeners
    };
  }
  static desugarFieldEventListeners(unsugarable) {
    if (unsugarable.onBeforeUpdate === void 0 && unsugarable.onUpdate === void 0) {
      return void 0;
    }
    const store = new EventListenersStore();
    const beforeUpdate = this.desugarEventListener(unsugarable.onBeforeUpdate);
    const update = this.desugarEventListener(unsugarable.onUpdate);
    if (beforeUpdate) {
      store.set({ type: "beforeUpdate" }, beforeUpdate);
    }
    if (update) {
      store.set({ type: "update" }, update);
    }
    return store;
  }
  static desugarSubTreeAlias(alias) {
    if (alias === void 0) {
      return void 0;
    }
    if (alias instanceof Set) {
      return alias;
    }
    return (/* @__PURE__ */ new Set()).add(alias);
  }
  static desugarHasOneRelation(sugarable, unsugarable, environment) {
    return {
      field: sugarable.field,
      filter: sugarable.filter ? this.desugarFilter(sugarable.filter, environment) : void 0,
      expectedMutation: unsugarable.expectedMutation ?? RelationDefaults.expectedMutation,
      reducedBy: sugarable.reducedBy ? this.desugarUniqueWhere(sugarable.reducedBy, environment) : void 0,
      setOnCreate: unsugarable.setOnCreate ? this.desugarSetOnCreate(unsugarable.setOnCreate, environment) : void 0,
      isNonbearing: unsugarable.isNonbearing ?? EntityCreationParametersDefaults.isNonbearing,
      // forceCreation: unsugarable.forceCreation ?? EntityCreationParametersDefaults.forceCreation,
      eventListeners: this.desugarSingleEntityEventListeners(unsugarable)
    };
  }
  static augmentDesugaredHasOneRelationPath(path, unsugarable, environment) {
    return path.map(
      (desugaredHasOneRelation, i) => (
        // Unsugarable applies to the last
        this.desugarHasOneRelation(desugaredHasOneRelation, i === path.length - 1 ? unsugarable : {}, environment)
      )
    );
  }
  static augmentDesugaredHasManyRelation(relation, unsugarable, environment) {
    const eventListeners = this.desugarEntityListEventListeners(unsugarable);
    return {
      field: relation.field,
      filter: relation.filter,
      expectedMutation: unsugarable.expectedMutation ?? RelationDefaults.expectedMutation,
      isNonbearing: unsugarable.isNonbearing ?? EntityCreationParametersDefaults.isNonbearing,
      // forceCreation: unsugarable.forceCreation ?? EntityCreationParametersDefaults.forceCreation,
      initialEntityCount: unsugarable.initialEntityCount ?? EntityListPreferencesDefaults.initialEntityCount,
      setOnCreate: unsugarable.setOnCreate ? this.desugarSetOnCreate(unsugarable.setOnCreate, environment) : void 0,
      orderBy: unsugarable.orderBy ? this.desugarOrderBy(unsugarable.orderBy, environment) : void 0,
      offset: unsugarable.offset,
      limit: unsugarable.limit,
      childEventListeners: eventListeners?.childEventListeners,
      eventListeners: eventListeners?.eventListeners
    };
  }
  static desugarHasOneRelationPath(input, lastRelation, environment) {
    if (input === void 0) {
      input = [];
    } else if (!Array.isArray(input)) {
      input = [input];
    }
    const relationPath = [];
    const cappedLength = input.length - 1;
    let i = 0;
    for (; i < cappedLength; i++) {
      const pathNode = input[i];
      relationPath.push(this.desugarHasOneRelation(pathNode, {}, environment));
    }
    if (i in input) {
      relationPath.push(this.desugarHasOneRelation(input[i], lastRelation, environment));
    }
    return relationPath;
  }
  static desugarHasManyRelation(sugarablePart, unsugarablePart, environment) {
    const eventListeners = this.desugarEntityListEventListeners(unsugarablePart);
    return {
      ...this.desugarEntityListParameters(sugarablePart, unsugarablePart, environment),
      expectedMutation: unsugarablePart.expectedMutation ?? RelationDefaults.expectedMutation,
      setOnCreate: unsugarablePart.setOnCreate ? this.desugarSetOnCreate(unsugarablePart.setOnCreate, environment) : void 0,
      field: sugarablePart.field,
      isNonbearing: unsugarablePart.isNonbearing ?? EntityCreationParametersDefaults.isNonbearing,
      // forceCreation: unsugarablePart.forceCreation ?? EntityCreationParametersDefaults.forceCreation,
      childEventListeners: eventListeners?.childEventListeners,
      eventListeners: eventListeners?.eventListeners
    };
  }
  static desugarUnconstrainedQualifiedEntityList({ entities, ...unsugarableEntityList }, environment) {
    let hasOneRelationPath;
    let entityName;
    if (typeof entities === "string") {
      const parsed = this.parseUnconstrainedQualifiedEntityList(entities, environment);
      entityName = parsed.entityName;
      hasOneRelationPath = this.augmentDesugaredHasOneRelationPath(
        parsed.hasOneRelationPath,
        emptyObject,
        environment
      );
    } else {
      entityName = entities.entityName;
      hasOneRelationPath = this.desugarHasOneRelationPath(entities.hasOneRelationPath, emptyObject, environment);
    }
    const eventListeners = this.desugarEntityListEventListeners(unsugarableEntityList);
    return {
      isCreating: true,
      isNonbearing: unsugarableEntityList.isNonbearing ?? EntityCreationParametersDefaults.isNonbearing,
      // forceCreation: unsugarableEntityList.forceCreation ?? EntityCreationParametersDefaults.forceCreation,
      setOnCreate: unsugarableEntityList.setOnCreate ? this.desugarSetOnCreate(unsugarableEntityList.setOnCreate, environment) : void 0,
      expectedMutation: unsugarableEntityList.expectedMutation ?? QualifiedEntityParametersDefaults.expectedMutation,
      childEventListeners: eventListeners?.childEventListeners,
      eventListeners: eventListeners?.eventListeners,
      initialEntityCount: unsugarableEntityList.initialEntityCount ?? EntityListPreferencesDefaults.initialEntityCount,
      alias: this.desugarSubTreeAlias(unsugarableEntityList.alias),
      entityName,
      hasOneRelationPath
    };
  }
  static desugarUnconstrainedQualifiedSingleEntity({ entity, ...unsugarableSingleEntity }, environment) {
    let hasOneRelationPath;
    let entityName;
    if (typeof entity === "string") {
      const parsed = this.parseUnconstrainedQualifiedSingleEntity(entity, environment);
      entityName = parsed.entityName;
      hasOneRelationPath = this.augmentDesugaredHasOneRelationPath(
        parsed.hasOneRelationPath,
        unsugarableSingleEntity,
        environment
      );
    } else {
      entityName = entity.entityName;
      hasOneRelationPath = this.desugarHasOneRelationPath(
        entity.hasOneRelationPath,
        unsugarableSingleEntity,
        environment
      );
    }
    return {
      isCreating: true,
      isNonbearing: unsugarableSingleEntity.isNonbearing ?? EntityCreationParametersDefaults.isNonbearing,
      // forceCreation: unsugarableSingleEntity.forceCreation ?? EntityCreationParametersDefaults.forceCreation,
      setOnCreate: unsugarableSingleEntity.setOnCreate ? this.desugarSetOnCreate(unsugarableSingleEntity.setOnCreate, environment) : void 0,
      expectedMutation: unsugarableSingleEntity.expectedMutation ?? QualifiedEntityParametersDefaults.expectedMutation,
      eventListeners: this.desugarSingleEntityEventListeners(unsugarableSingleEntity),
      alias: this.desugarSubTreeAlias(unsugarableSingleEntity.alias),
      entityName,
      hasOneRelationPath
    };
  }
  static desugarQualifiedEntityList({ entities, ...unsugarableEntityList }, environment) {
    let entityName;
    let hasOneRelationPath;
    let filter;
    if (typeof entities === "string") {
      const parsed = this.parseQualifiedEntityList(entities, environment);
      entityName = parsed.entityName;
      filter = parsed.filter;
      hasOneRelationPath = this.augmentDesugaredHasOneRelationPath(
        parsed.hasOneRelationPath,
        emptyObject,
        environment
      );
    } else {
      entityName = entities.entityName;
      filter = entities.filter;
      hasOneRelationPath = this.desugarHasOneRelationPath(entities.hasOneRelationPath, emptyObject, environment);
    }
    const eventListeners = this.desugarEntityListEventListeners(unsugarableEntityList);
    return {
      entityName,
      hasOneRelationPath,
      ...this.desugarEntityListParameters(
        {
          filter
        },
        unsugarableEntityList,
        environment
      ),
      alias: this.desugarSubTreeAlias(unsugarableEntityList.alias),
      isCreating: false,
      isNonbearing: unsugarableEntityList.isNonbearing ?? EntityCreationParametersDefaults.isNonbearing,
      // forceCreation: unsugarableEntityList.forceCreation ?? EntityCreationParametersDefaults.forceCreation,
      setOnCreate: unsugarableEntityList.setOnCreate ? this.desugarSetOnCreate(unsugarableEntityList.setOnCreate, environment) : void 0,
      expectedMutation: unsugarableEntityList.expectedMutation ?? QualifiedEntityParametersDefaults.expectedMutation,
      childEventListeners: eventListeners?.childEventListeners,
      eventListeners: eventListeners?.eventListeners
    };
  }
  static desugarQualifiedFieldList({ fields, ...unsugarableFieldList }, environment) {
    let field;
    let entityName;
    let filter;
    let hasOneRelationPath;
    if (typeof fields === "string") {
      const parsed = this.parseQualifiedFieldList(fields, environment);
      field = parsed.field;
      entityName = parsed.entityName;
      filter = parsed.filter;
      hasOneRelationPath = this.augmentDesugaredHasOneRelationPath(
        parsed.hasOneRelationPath,
        emptyObject,
        environment
      );
    } else {
      field = fields.field;
      entityName = fields.entityName;
      filter = fields.filter;
      hasOneRelationPath = this.desugarHasOneRelationPath(fields.hasOneRelationPath, emptyObject, environment);
    }
    return {
      field,
      entityName,
      hasOneRelationPath,
      alias: this.desugarSubTreeAlias(unsugarableFieldList.alias),
      isNonbearing: unsugarableFieldList.isNonbearing ?? EntityCreationParametersDefaults.isNonbearing,
      // forceCreation: unsugarableFieldList.forceCreation ?? EntityCreationParametersDefaults.forceCreation,
      defaultValue: unsugarableFieldList.defaultValue !== void 0 ? VariableInputTransformer.transformValue(unsugarableFieldList.defaultValue, environment) : void 0,
      expectedMutation: unsugarableFieldList.expectedMutation ?? QualifiedEntityParametersDefaults.expectedMutation,
      eventListeners: this.desugarFieldEventListeners(unsugarableFieldList),
      ...this.desugarEntityListParameters({ filter }, unsugarableFieldList, environment)
    };
  }
  static desugarQualifiedSingleEntity({ entity, ...unsugarableSingleEntity }, environment, options = {}) {
    let entityName;
    let where;
    let filter;
    let hasOneRelationPath;
    if (typeof entity === "string") {
      const parsed = this.parseQualifiedSingleEntity(entity, environment);
      entityName = parsed.entityName;
      where = parsed.where;
      filter = parsed.filter;
      hasOneRelationPath = this.augmentDesugaredHasOneRelationPath(
        parsed.hasOneRelationPath,
        unsugarableSingleEntity,
        environment
      );
    } else {
      entityName = entity.entityName;
      where = this.desugarUniqueWhere(entity.where, environment);
      filter = entity.filter ? this.desugarFilter(entity.filter, environment) : void 0;
      hasOneRelationPath = this.desugarHasOneRelationPath(
        entity.hasOneRelationPath,
        unsugarableSingleEntity,
        environment
      );
    }
    let setOnCreate = unsugarableSingleEntity.setOnCreate !== void 0 ? this.desugarSetOnCreate(unsugarableSingleEntity.setOnCreate, environment) : void 0;
    if (!setOnCreate && options.missingSetOnCreate) {
      const whereValues = Object.values(where);
      if (whereValues.length === 1 && whereValues[0] instanceof GraphQlLiteral) {
        setOnCreate = where;
      }
    }
    const isNonbearing = unsugarableSingleEntity.isNonbearing !== void 0 ? unsugarableSingleEntity.isNonbearing : EntityCreationParametersDefaults.isNonbearing;
    const eventListeners = this.desugarSingleEntityEventListeners(unsugarableSingleEntity);
    const expectedMutation = unsugarableSingleEntity.expectedMutation ?? QualifiedEntityParametersDefaults.expectedMutation;
    const alias = this.desugarSubTreeAlias(unsugarableSingleEntity.alias);
    return {
      alias,
      entityName,
      isCreating: false,
      where,
      filter,
      hasOneRelationPath,
      // forceCreation,
      setOnCreate,
      isNonbearing,
      eventListeners,
      expectedMutation
    };
  }
  static desugarParentEntityParameters(parentEntity, environment) {
    return {
      eventListeners: this.desugarSingleEntityEventListeners(parentEntity)
    };
  }
  static desugarRelativeSingleEntity(sugaredRelativeSingleEntity, environment) {
    if (typeof sugaredRelativeSingleEntity === "string") {
      return this.desugarRelativeSingleEntity({ field: sugaredRelativeSingleEntity }, environment);
    }
    const { field, ...unsugarableEntity } = sugaredRelativeSingleEntity;
    let hasOneRelationPath;
    if (typeof field === "string") {
      const parsed = this.parseRelativeSingleEntity(field, environment);
      hasOneRelationPath = this.augmentDesugaredHasOneRelationPath(
        parsed.hasOneRelationPath,
        unsugarableEntity,
        environment
      );
    } else {
      hasOneRelationPath = this.desugarHasOneRelationPath(field, unsugarableEntity, environment);
    }
    return {
      hasOneRelationPath
    };
  }
  static desugarRelativeSingleField(sugaredRelativeSingleField, environment) {
    if (typeof sugaredRelativeSingleField === "string") {
      return this.desugarRelativeSingleField({ field: sugaredRelativeSingleField }, environment);
    }
    const { field, ...unsugarableField } = sugaredRelativeSingleField;
    let hasOneRelationPath;
    let fieldName;
    if (typeof field === "string") {
      const parsed = this.parseRelativeSingleField(field, environment);
      hasOneRelationPath = this.augmentDesugaredHasOneRelationPath(parsed.hasOneRelationPath, {}, environment);
      fieldName = parsed.field;
    } else {
      hasOneRelationPath = this.desugarHasOneRelationPath(field.hasOneRelationPath, {}, environment);
      fieldName = field.field;
    }
    return {
      eventListeners: this.desugarFieldEventListeners(unsugarableField),
      hasOneRelationPath,
      field: fieldName,
      isNonbearing: unsugarableField.isNonbearing ?? LeafFieldDefaults.isNonbearing,
      defaultValue: unsugarableField.defaultValue !== void 0 ? VariableInputTransformer.transformValue(unsugarableField.defaultValue, environment) : void 0
    };
  }
  static desugarRelativeEntityList(sugaredRelativeEntityList, environment) {
    if (typeof sugaredRelativeEntityList === "string") {
      return this.desugarRelativeEntityList(
        {
          field: sugaredRelativeEntityList
        },
        environment
      );
    }
    const { field, ...unsugarableEntityList } = sugaredRelativeEntityList;
    let hasOneRelationPath;
    let hasManyRelation;
    if (typeof field === "string") {
      const parsed = this.parseRelativeEntityList(field, environment);
      hasOneRelationPath = this.augmentDesugaredHasOneRelationPath(
        parsed.hasOneRelationPath,
        emptyObject,
        environment
      );
      hasManyRelation = this.augmentDesugaredHasManyRelation(
        parsed.hasManyRelation,
        unsugarableEntityList,
        environment
      );
    } else {
      hasOneRelationPath = this.desugarHasOneRelationPath(field.hasOneRelationPath || [], emptyObject, environment);
      hasManyRelation = this.desugarHasManyRelation(field.hasManyRelation, unsugarableEntityList, environment);
    }
    return {
      hasManyRelation,
      hasOneRelationPath
    };
  }
};
let QueryLanguage = _QueryLanguage;
__publicField(QueryLanguage, "parseUnconstrainedQualifiedEntityList", _QueryLanguage.preparePrimitiveEntryPoint("unconstrainedQualifiedEntityList"));
__publicField(QueryLanguage, "parseUnconstrainedQualifiedSingleEntity", _QueryLanguage.preparePrimitiveEntryPoint("unconstrainedQualifiedEntityList"));
__publicField(QueryLanguage, "parseQualifiedEntityList", _QueryLanguage.preparePrimitiveEntryPoint("qualifiedEntityList"));
__publicField(QueryLanguage, "parseQualifiedFieldList", _QueryLanguage.preparePrimitiveEntryPoint("qualifiedFieldList"));
__publicField(QueryLanguage, "parseQualifiedSingleEntity", _QueryLanguage.preparePrimitiveEntryPoint("qualifiedSingleEntity"));
__publicField(QueryLanguage, "parseRelativeEntityList", _QueryLanguage.preparePrimitiveEntryPoint("relativeEntityList"));
__publicField(QueryLanguage, "parseRelativeSingleEntity", _QueryLanguage.preparePrimitiveEntryPoint("relativeSingleEntity"));
__publicField(QueryLanguage, "parseRelativeSingleField", _QueryLanguage.preparePrimitiveEntryPoint("relativeSingleField"));
__publicField(QueryLanguage, "desugarUniqueWhere", _QueryLanguage.preparePrimitiveEntryPointWithFallback("uniqueWhere"));
__publicField(QueryLanguage, "desugarFilter", _QueryLanguage.preparePrimitiveEntryPointWithFallback("filter"));
__publicField(QueryLanguage, "desugarOrderBy", _QueryLanguage.preparePrimitiveEntryPointWithFallback("orderBy"));
__publicField(QueryLanguage, "desugarTaggedMap", _QueryLanguage.preparePrimitiveEntryPointWithFallback("taggedMap"));
export {
  QueryLanguage
};
//# sourceMappingURL=QueryLanguage.js.map
